import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../lib/apiClient';
import LanguageSelector from './LanguageSelector';
import { AnimatedPage } from './motion';

export default function SettingsPage({ onClose, isDark, onToggleTheme, theme, onSetTheme, onResetToSystem, onLanguageChange }) {
  const { t } = useTranslation();
  const [subView, setSubView] = useState(null); // 'appearance' | 'language' | 'dataPrivacy'

  const title = subView
    ? t(`settings.${subView === 'dataPrivacy' ? 'dataPrivacy' : subView}`)
    : t('menu.settings');

  const goBack = () => subView ? setSubView(null) : onClose();

  return (
    <AnimatedPage className="fixed inset-0 z-[var(--aif-z-page-fullscreen)] bg-[var(--aif-bg-page)] flex flex-col">
      <header className="flex items-center gap-3 px-4 py-3 border-b border-[var(--aif-border-default)] flex-shrink-0">
        <button onClick={goBack} className="w-10 h-10 flex items-center justify-center rounded-xl hover:bg-[var(--aif-bg-surface)] transition text-[var(--aif-text-muted)]">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15.75 19.5L8.25 12l7.5-7.5" /></svg>
        </button>
        <span className="text-sm font-semibold text-[var(--aif-text-primary)]">{title}</span>
      </header>

      <div className="flex-1 overflow-y-auto">
        {!subView && <HubView onSelect={setSubView} t={t} />}
        {subView === 'appearance' && <AppearanceView isDark={isDark} onToggleTheme={onToggleTheme} theme={theme} onSetTheme={onSetTheme} onResetToSystem={onResetToSystem} t={t} />}
        {subView === 'language' && <LanguageView onLanguageChange={onLanguageChange} t={t} />}
        {subView === 'dataPrivacy' && <DataPrivacyView t={t} />}
      </div>
    </AnimatedPage>
  );
}

function HubView({ onSelect, t }) {
  const items = [
    { key: 'appearance', icon: <PaletteIcon />, label: t('settings.appearance') },
    { key: 'language', icon: <GlobeIcon />, label: t('settings.language') },
    { key: 'dataPrivacy', icon: <ShieldIcon />, label: t('settings.dataPrivacy') },
  ];

  return (
    <div className="max-w-lg mx-auto py-2">
      {items.map(item => (
        <button
          key={item.key}
          onClick={() => onSelect(item.key)}
          className="w-full flex items-center gap-4 px-6 py-4 text-left hover:bg-[var(--aif-bg-surface)] transition active:bg-[var(--aif-bg-elevated)]"
        >
          <span className="w-5 h-5 text-[var(--aif-text-muted)]">{item.icon}</span>
          <span className="text-sm text-[var(--aif-text-primary)] flex-1">{item.label}</span>
          <svg className="w-4 h-4 text-[var(--aif-text-muted)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8.25 4.5l7.5 7.5-7.5 7.5" />
          </svg>
        </button>
      ))}
    </div>
  );
}

function AppearanceView({ isDark, onToggleTheme, theme, onSetTheme, onResetToSystem, t }) {
  const options = [
    { key: 'light', label: t('settings.themeLight') },
    { key: 'dark', label: t('settings.themeDark') },
    { key: 'system', label: t('settings.themeSystem', 'System') },
    { key: 'outdoor', label: t('settings.themeOutdoor', 'Outdoor') },
  ];

  const isSystem = !localStorage.getItem('aif_theme');
  const activeKey = isSystem ? 'system' : theme;

  const handleSelect = (key) => {
    if (key === 'system') {
      if (onResetToSystem) onResetToSystem();
    } else if (onSetTheme) {
      onSetTheme(key);
    } else {
      // Fallback to legacy toggle if new props not available
      if ((key === 'dark') !== isDark) onToggleTheme();
    }
  };

  return (
    <div className="max-w-lg mx-auto px-6 py-6 space-y-4">
      <p className="text-sm text-[var(--aif-text-muted)]">{t('settings.appearanceHint')}</p>
      <div className="flex flex-wrap gap-3">
        {options.map(opt => (
          <button
            key={opt.key}
            onClick={() => handleSelect(opt.key)}
            className={`flex-1 min-w-[calc(50%-0.375rem)] py-3 text-sm font-medium rounded-xl border transition ${
              activeKey === opt.key
                ? 'border-[var(--aif-color-primary)] bg-[var(--aif-color-primary)]/10 text-[var(--aif-color-primary)]'
                : 'border-[var(--aif-border-default)] bg-[var(--aif-bg-surface)] text-[var(--aif-text-secondary)]'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function LanguageView({ onLanguageChange, t }) {
  return (
    <div className="max-w-lg mx-auto px-6 py-6 space-y-4">
      <p className="text-sm text-[var(--aif-text-muted)]">{t('settings.languageHint')}</p>
      <LanguageSelector onLanguageChange={onLanguageChange} />
    </div>
  );
}

function DataPrivacyView({ t }) {
  const [exporting, setExporting] = useState(false);
  const [deleteStatus, setDeleteStatus] = useState(null); // null | 'confirming' | 'scheduled' | 'error'
  const [deleteInfo, setDeleteInfo] = useState(null);

  const handleExport = async () => {
    setExporting(true);
    try {
      // Use new ZIP export endpoint; fall back to JSON if ZIP unavailable
      const authToken = localStorage.getItem('aif_token');
      const res = await fetch('/api/v1/aif/export/my-data', {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        // Try to get filename from Content-Disposition header
        const cd = res.headers.get('Content-Disposition') || '';
        const match = cd.match(/filename="([^"]+)"/);
        a.download = match?.[1] || 'AI_Farm_Export.zip';
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch { /* ignore */ }
    setExporting(false);
  };

  const handleDeleteRequest = () => {
    setDeleteStatus('confirming');
  };

  const handleDeleteConfirm = async () => {
    try {
      const data = await apiClient.delete('/api/v1/aif/auth/account', { queueOnOffline: true }); // TODO(5b): confirm 3WM — no PATCH/DELETE ETag concurrency yet
      setDeleteInfo(data);
      setDeleteStatus('scheduled');
    } catch {
      setDeleteStatus('error');
    }
  };

  return (
    <div className="max-w-lg mx-auto px-6 py-6 space-y-2">
      <p className="text-sm text-[var(--aif-text-muted)] mb-4">{t('settings.dataPrivacyHint')}</p>

      {/* Export ZIP */}
      <button
        onClick={handleExport}
        disabled={exporting}
        className="w-full flex items-center gap-4 px-4 py-3.5 rounded-xl hover:bg-[var(--aif-bg-surface)] transition text-left"
      >
        <span className="w-5 h-5 text-[var(--aif-text-muted)]"><DownloadIcon /></span>
        <div>
          <div className="text-sm text-[var(--aif-text-primary)]">{exporting ? '…' : t('settings.exportData')}</div>
          <div className="text-[10px] text-[var(--aif-text-muted)]">{t('settings.exportDataHint')}</div>
        </div>
      </button>

      <button
        onClick={() => window.location.href = '/datenschutz'}
        className="w-full flex items-center gap-4 px-4 py-3.5 rounded-xl hover:bg-[var(--aif-bg-surface)] transition text-left"
      >
        <span className="w-5 h-5 text-[var(--aif-text-muted)]"><ShieldIcon /></span>
        <span className="text-sm text-[var(--aif-text-primary)]">{t('menu.privacy')}</span>
      </button>

      {/* Account deletion with grace period */}
      {deleteStatus === null && (
        <button
          onClick={handleDeleteRequest}
          className="w-full flex items-center gap-4 px-4 py-3.5 rounded-xl hover:bg-[var(--aif-bg-surface)] transition text-left"
        >
          <span className="w-5 h-5 text-[var(--aif-color-danger)]"><TrashIcon /></span>
          <span className="text-sm text-[var(--aif-color-danger)]">{t('settings.deleteAccount')}</span>
        </button>
      )}
      {deleteStatus === 'confirming' && (
        <div className="rounded-xl border border-red-500/30 bg-red-500/5 p-4 space-y-3">
          <p className="text-sm text-red-400 font-medium">{t('settings.deleteConfirmTitle')}</p>
          <p className="text-xs text-[var(--aif-text-muted)] leading-relaxed">{t('settings.deleteGracePeriodHint')}</p>
          <div className="flex gap-2">
            <button
              onClick={handleDeleteConfirm}
              className="flex-1 px-4 py-2 rounded-xl bg-red-500/20 text-red-400 text-sm font-medium border border-red-500/30 hover:bg-red-500/30 transition"
            >
              {t('settings.deleteConfirmBtn')}
            </button>
            <button
              onClick={() => setDeleteStatus(null)}
              className="flex-1 px-4 py-2 rounded-xl bg-[var(--aif-bg-elevated)] text-[var(--aif-text-secondary)] text-sm border border-[var(--aif-border-default)] hover:bg-[var(--aif-bg-surface)] transition"
            >
              {t('settings.deleteCancel')}
            </button>
          </div>
        </div>
      )}
      {deleteStatus === 'scheduled' && deleteInfo && (
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4">
          <p className="text-sm text-amber-400 font-medium mb-1">⏳ {t('settings.deleteScheduled')}</p>
          <p className="text-xs text-[var(--aif-text-muted)] leading-relaxed">{deleteInfo.message}</p>
        </div>
      )}
      {deleteStatus === 'error' && (
        <p className="text-xs text-red-400 px-4">{t('settings.deleteError')}</p>
      )}
    </div>
  );
}

// Icons
function PaletteIcon() {
  return <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4.098 19.902a3.75 3.75 0 005.304 0l6.401-6.402M6.75 21A3.75 3.75 0 013 17.25V4.125C3 3.504 3.504 3 4.125 3h5.25c.621 0 1.125.504 1.125 1.125v4.072M6.75 21a3.75 3.75 0 003.75-3.75V8.197M6.75 21h13.125c.621 0 1.125-.504 1.125-1.125v-5.25c0-.621-.504-1.125-1.125-1.125h-4.072M10.5 8.197l2.88-2.88c.438-.439 1.15-.439 1.59 0l3.712 3.713c.44.44.44 1.152 0 1.59l-2.879 2.88M6.75 17.25h.008v.008H6.75v-.008z" /></svg>;
}
function GlobeIcon() {
  return <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418" /></svg>;
}
function ShieldIcon() {
  return <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" /></svg>;
}
function DownloadIcon() {
  return <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" /></svg>;
}
function TrashIcon() {
  return <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" /></svg>;
}
